import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import {observer} from 'mobx-react-lite'
import {useLocation, useNavigate} from 'react-router'
import {keyBoardPressActions} from '@oc/utils'
import {SlideUp} from '../SlideUp'
import {ValidationState} from './ValidationState'
import {useGlobalDiscard, useGlobalSave} from '../saveableStateHelper'
import {useEventBusChannel} from '../EventBus'
import {SaveableStateEvents} from '../saveableStateEvents'
import {messages} from '../texts'
import {definedContextValueHookFactory, useInject} from '@oc/hooks'

const ValidationContext = React.createContext<ValidationState | null>(null)

export const useValidationState = definedContextValueHookFactory(ValidationContext, 'ValidationContext')

interface HasValidationState {
    validationState?: ValidationState
}

/**
 * Ensures that `state.validationState` is kept up to date with state returned by `useValidationState`
 */
export function useInjectValidationState(state: HasValidationState) {
    useInject(state, 'validationState', useValidationState)
}

export function ValidationContextProvider(props: React.PropsWithChildren) {
    const [state] = React.useState(() => new ValidationState())

    useInject(state, 'navigate', useNavigate)
    useInject(state, 'location', useLocation)

    return (
        <ValidationContext.Provider value={state}>
            {props.children}
            <ValidationErrorDialog />
        </ValidationContext.Provider>
    )
}

function _ValidationErrorDialog() {
    const discard = useGlobalDiscard()
    const save = useGlobalSave()
    const validationState = useValidationState()

    const discardWrapped = React.useCallback(
        () => validationState.doActionAndRoute(discard),
        [validationState, discard]
    )
    const saveWrapped = React.useCallback(() => validationState.doActionAndRoute(save), [validationState, save])
    const closeWrapped = React.useCallback(() => {
        validationState.clearRoute()
        validationState.clearErrorMessage()
    }, [validationState])

    const eventBusChannel = useEventBusChannel<SaveableStateEvents>()
    const globalSaveDiscardHasListeners =
        eventBusChannel.listenerCount('save') + eventBusChannel.listenerCount('discard') > 0
    if (globalSaveDiscardHasListeners) {
        // TODO render this unconditionally when new OC views are finished
        return (
            <div>
                <Dialog
                    onKeyUp={event => {
                        keyBoardPressActions(
                            event,
                            validationState.clearErrorMessage,
                            saveWrapped,
                            undefined,
                            undefined,
                            !!validationState.errorMessage,
                            undefined
                        )
                    }}
                    open={!!validationState.errorMessage}
                    TransitionComponent={SlideUp}
                    onClose={closeWrapped}
                    keepMounted
                >
                    <DialogTitle>{validationState.errorMessage}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {messages.getText('validationErrorDialog', 'dialogHeader')}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeWrapped} color="primary" key="Cancel">
                            {messages.getText('validationErrorDialog', 'cancel')}
                        </Button>
                        <Button onClick={discardWrapped} color="primary" key="Discard">
                            {messages.getText('validationErrorDialog', 'discard')}
                        </Button>
                        <Button onClick={saveWrapped} color="primary" key="Save">
                            {messages.getText('validationErrorDialog', 'save')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        )
    }

    return (
        <div>
            <Dialog
                onKeyUp={event => {
                    keyBoardPressActions(
                        event,
                        validationState.clearErrorMessage,
                        () => {
                            validationState.actions?.forEach(action => {
                                if (action.label === 'Save' && action.callback) action.callback()
                            })
                        },
                        undefined,
                        undefined,
                        !!validationState.errorMessage,
                        undefined
                    )
                }}
                open={!!validationState.errorMessage}
                TransitionComponent={SlideUp}
                onClose={validationState.clearErrorMessage}
                keepMounted
            >
                <DialogTitle>{validationState.errorMessage}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{messages.getText('validationErrorDialog', 'dialogHeader')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={validationState.clearErrorMessage} color="primary" key="Cancel">
                        {messages.getText('validationErrorDialog', 'cancel')}
                    </Button>
                    {validationState.actions?.map(action => (
                        <Button onClick={action.callback} color="primary" key={action.label}>
                            {action.label}
                        </Button>
                    ))}
                </DialogActions>
            </Dialog>
        </div>
    )
}

export const ValidationErrorDialog = observer(_ValidationErrorDialog)
